<template>
  <hb-basic-page
    :title="$t('items.title')"
    :search-title="`${$t('items.search')}...`"
    :initial-search="listState.q"
    @handle-search="debounceSearch"
    fluid
    :loading="loading"
    :extension-height="60"
    class="sites-view"
    :pa-0="$vuetify.breakpoint.xsOnly"
    v-if="$isTenantAdmin"
  >
    <template v-if="$vuetify.breakpoint.smAndUp" v-slot:app-bar-extension>
      <v-btn @click="addItem()" color="primary" class="mb-3 ml-n1">
        <v-icon class="no-gradient" left>mdi-plus</v-icon>
        <span>{{ $t('items.create_new_item') }}</span>
      </v-btn>
      <v-spacer />
      <v-btn @click="downloadItemsExcel" color="primary" class="mb-3 mr-3" :loading="exportingExcel">
        <v-icon class="no-gradient" left>mdi-file-excel</v-icon>
        {{ $t('items.export_excel') }}
      </v-btn>
    </template>

    <v-btn v-if="$vuetify.breakpoint.xsOnly" @click="addItem()" color="primary" fab fixed bottom right>
      <v-icon class="no-gradient">mdi-plus</v-icon>
    </v-btn>
    <v-btn v-if="$vuetify.breakpoint.xsOnly" @click="downloadItemsExcel" color="primary" outlined fixed bottom right style="bottom: 80px" :loading="exportingExcel">
      <v-icon left>mdi-file-excel</v-icon>
      {{ $t('items.export_excel') }}
    </v-btn>

    <v-col cols="12" class="gradient-icons mb-12">
      <!-- Haku on yläpalkin hakukentässä (BasicPage); tyyppirajaus pysyy näkyvissä listaa vieritettäessä. -->
      <div class="items-filter main-bg py-2" :style="{ top: $vuetify.application.top + 'px' }">
        <item-type-chips v-model="typeFilter" :types="typesWithItems" />
      </div>
      <v-fade-transition>
        <v-row no-gutters>
          <v-col cols="12" class="mt-4" v-for="group in groups" :key="group.type">
            <v-row>
              <items-view-item-type
                :no-content="$t('items.no_items_in_type')"
                :items="group.items"
                :item-type-id="group.type"
                :expanded="filtering || openTypes.includes(group.type)"
                @toggle="toggleType(group.type)"
                @open-item-remove-dialog="openItemRemoveDialog"
                @open-item="openItem"
                @open-new="addItem"
              />
            </v-row>
          </v-col>
          <v-col v-if="groups.length === 0" cols="12" class="mt-4 text-center grey--text">
            {{ $t('items.no_items') }}
          </v-col>
          <confirm-dialog ref="openItemRemoveDialog" />
          <add-or-edit-item-dialog
            ref="addOrEditItemDialog"
            @updated="itemUpdatedSnackbar = true && reloadItems()"
            @created="itemCreatedSnackbar = true && reloadItems()"
          />

          <v-snackbar v-model="itemDeletedSnackbar" :timeout="timeOut" top elevation="3" color="error">{{
            $t('items.item_deleted')
          }}</v-snackbar>
          <v-snackbar v-model="itemCreatedSnackbar" :timeout="timeOut" top elevation="3" color="success">{{
            $t('items.item_created')
          }}</v-snackbar>
          <v-snackbar v-model="itemUpdatedSnackbar" :timeout="timeOut" top elevation="3" color="success">{{
            $t('items.item_updated')
          }}</v-snackbar>
        </v-row>
      </v-fade-transition>
    </v-col>
  </hb-basic-page>
</template>

<script>
import itemApi from '@/api/item';
import ItemsViewItemType from '@/components/Items/ItemsViewItemType';
import ItemTypeChips from '@/components/ItemTypeChips';
import ConfirmDialog from '@/components/ConfirmDialog';
import AddOrEditItemDialog from '@/components/Items/AddOrEditItemDialog';
import { allItemTypes, loadItemTypes, typeIds, typePluralName } from '@/helpers/itemTypes';
import listQuery from '@/helpers/listQuery';
import { matchesWords, searchWords } from '@/helpers/textSearch';
import debounce from 'lodash.debounce';

// Tyyppirajaus (tyypin id) ja hakusana pidetään URL:ssa, jotta nimikkeen muokkauksesta ja takaisin-nuolella palataan samaan rajaukseen.
const QUERY_FIELDS = {
  type: { type: 'int', default: null, nullable: true },
  q: { type: 'string', default: '' },
};

export default {
  name: 'ItemsView',
  components: { AddOrEditItemDialog, ConfirmDialog, ItemsViewItemType, ItemTypeChips },
  mixins: [listQuery({ key: 'items', fields: QUERY_FIELDS })],
  data() {
    return {
      loading: true,
      items: [],
      timeOut: 2000,
      itemDeletedSnackbar: false,
      itemCreatedSnackbar: false,
      itemUpdatedSnackbar: false,
      exportingExcel: false,
      // Käsin avatut kortit Kaikki-tilassa; haku tai tyyppirajaus avaa kortit aina.
      openTypes: [],
    };
  },
  created() {
    this.debounceSearch = debounce(this.handleSearch, 400, { maxWait: 1000 });
  },
  computed: {
    typeFilter: {
      get() {
        return this.listState.type;
      },
      set(type) {
        this.updateQuery({ type: type ?? null });
      },
    },
    words() {
      return searchWords(this.listState.q);
    },
    filtering() {
      return this.words.length > 0 || !!this.typeFilter;
    },
    typesWithItems() {
      return typeIds(type => this.items.some(item => item.typeId === type.id));
    },
    // Kortit tyyppijärjestyksessä. Hakusanalla näytetään vain osumia sisältävät tyypit; tyyppirajauksella
    // myös tyhjä tyyppi, jotta siihen pääsee lisäämään. Käytöstä poistettu tyyppi näkyy, jos sillä on nimikkeitä.
    groups() {
      const types = this.typeFilter
        ? [this.typeFilter]
        : allItemTypes()
            .filter(type => type.active || this.items.some(item => item.typeId === type.id))
            .map(type => type.id);
      return types
        .map(type => {
          const label = typePluralName(type);
          const items = this.items.filter(
            item => item.typeId === type && matchesWords([item.name, item.code, label].join(' '), this.words)
          );
          return { type, items };
        })
        .filter(group => group.items.length > 0 || this.words.length === 0);
    },
  },
  methods: {
    handleSearch(searchPhrase) {
      this.updateQuery({ q: searchPhrase || '' });
    },
    toggleType(type) {
      if (this.filtering) return;
      this.openTypes = this.openTypes.includes(type) ? this.openTypes.filter(t => t !== type) : [...this.openTypes, type];
    },
    openItem(itemId) {
      const newItem = false;
      this.$refs.addOrEditItemDialog.open(itemId, newItem);
    },
    openItemRemoveDialog(item) {
      this.$refs.openItemRemoveDialog
        .open(this.$t('items.delete_item_dialog.title'), this.$t('items.delete_item_dialog.description'), { color: 'error' })
        .then(confirm => {
          this.removeItem(confirm, item);
        });
    },
    async removeItem(confirm, item) {
      if (confirm === true) {
        const itemModified = {
          id: item.id,
          name: item.name,
          typeId: item.typeId,
          active: false,
        };
        await itemApi.removeItem(item.id, itemModified);
        this.itemDeletedSnackbar = true;
        await this.reloadItems();
      } else {
        this.itemDeletedSnackbar = false;
      }
    },
    addItem(typeId = null) {
      const site = null;
      const newItem = true;
      this.$refs.addOrEditItemDialog.open(site, newItem, typeId);
    },
    async downloadItemsExcel() {
      this.exportingExcel = true;
      try {
        await itemApi.exportItemsExcel();
      } catch (error) {
        console.error('Export failed:', error);
      } finally {
        this.exportingExcel = false;
      }
    },
    // listQuery kutsuu aina, kun URL:n query muuttuu; nimikkeet ladataan vain kerran, suodatus on paikallista.
    async reload() {
      if (!this.loading) return;
      await this.reloadItems();
      this.loading = false;
    },
    async reloadItems() {
      try {
        const [items] = await Promise.all([itemApi.getItems(), loadItemTypes()]);
        this.items = items;
      } catch (error) {
        console.log(error);
        this.$showErrorNotification(this.$tc('errors.no_items', 2), false);
      }
    },
  },
};
</script>

<style scoped>
/* Sama tausta kuin sivulla (v-application), jotta kortit eivät kuulla palkin läpi; sm-and-down saa valkoisen main-bg-luokasta. */
.items-filter {
  background-color: rgb(245, 246, 250);
  position: sticky;
  z-index: 2;
}
</style>
